import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/ui/DataTable';
import Modal from '../../components/ui/Modal';
import { useClient } from '../../context/ClientContext';
import { useAuth } from '../../context/AuthContext';

const TYPES = ['storage', 'quarantine', 'oversize'];
const typeChip = { storage: 'chip-blue', quarantine: 'chip-red', oversize: 'chip-amber' };

const emptyForm = { name: '', code: '', type: 'storage', floors: [{ label: '', capacity: '' }], client: '' };

const StorageSetup = () => {
  const { clients } = useClient();
  const { can } = useAuth();
  const canManage = can('storage.manage');

  const [racks, setRacks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]         = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchRacks = useCallback(() => {
    setLoading(true);
    api.get('/storage/racks')
      .then((r) => setRacks(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchRacks(); }, [fetchRacks]);

  const openCreate = () => { setEditTarget(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (rack) => {
    setEditTarget(rack);
    setForm({
      name: rack.name,
      code: rack.code,
      type: rack.type,
      floors: (rack.floors || []).length
        ? rack.floors.map((f) => ({ label: f.label, capacity: f.capacity ?? '' }))
        : [{ label: '', capacity: '' }],
      client: rack.client?._id || rack.client || '',
    });
    setShowForm(true);
  };

  const addFloor = () => setForm((f) => ({ ...f, floors: [...f.floors, { label: '', capacity: '' }] }));
  const removeFloor = (i) => setForm((f) => ({ ...f, floors: f.floors.filter((_, idx) => idx !== i) }));
  const updateFloor = (i, key, val) => setForm((f) => {
    const floors = [...f.floors];
    floors[i] = { ...floors[i], [key]: val };
    return { ...f, floors };
  });

  const handleDeactivate = async (rack) => {
    try {
      await api.patch(`/storage/racks/${rack._id}/deactivate`);
      toast.success('Rack deactivated');
      fetchRacks();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate rack');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      name: form.name,
      code: form.code,
      type: form.type,
      floors: form.floors
        .filter((f) => f.label.trim())
        .map((f) => ({ label: f.label.trim(), ...(f.capacity !== '' ? { capacity: Number(f.capacity) } : {}) })),
      client: form.client || undefined,
    };
    try {
      if (editTarget) {
        await api.put(`/storage/racks/${editTarget._id}`, payload);
        toast.success('Rack updated');
      } else {
        await api.post('/storage/racks', payload);
        toast.success('Rack created');
      }
      setShowForm(false);
      fetchRacks();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save rack');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { label: 'Name', key: 'name', render: (r) => <span className="font-medium text-zip-text">{r.name}</span> },
    { label: 'Code', key: 'code', render: (r) => <span className="font-mono text-zip-red text-xs font-semibold">{r.code}</span> },
    { label: 'Type', key: 'type', render: (r) => <span className={typeChip[r.type] || 'chip-gray'}>{r.type}</span> },
    {
      label: 'Floors', key: 'floors',
      render: (r) => (
        <span className="font-mono text-zip-muted text-xs">
          {(r.floors || []).length > 0 ? r.floors.map((f) => f.label).join(', ') : '—'}
        </span>
      ),
    },
    {
      label: 'Company', key: 'client',
      render: (r) => r.client
        ? <span className="text-zip-muted text-xs">{r.client.name || r.client}</span>
        : <span className="chip-blue">Global</span>,
    },
    {
      label: 'Status', key: 'isActive',
      render: (r) => <span className={r.isActive ? 'chip-green' : 'chip-gray'}>{r.isActive ? 'Active' : 'Inactive'}</span>,
    },
    ...(canManage ? [{
      label: '', key: 'actions',
      render: (r) => (
        <div className="flex items-center gap-3">
          <button onClick={() => openEdit(r)} className="text-xs font-mono text-zip-muted hover:text-zip-text transition-colors">Edit</button>
          {r.isActive && (
            <button onClick={() => handleDeactivate(r)} className="text-xs font-mono text-zip-muted hover:text-zip-red transition-colors">
              Deactivate
            </button>
          )}
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Storage Setup"
        subtitle="Configure racks and their floors for storage, quarantine, and oversize goods."
        action={canManage && (
          <button onClick={openCreate} className="btn-primary flex items-center gap-2">
            <Plus size={15} /> New Rack
          </button>
        )}
      />

      {loading ? (
        <div className="zip-card p-5 space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-10 skeleton rounded" />)}
        </div>
      ) : (
        <DataTable columns={columns} rows={racks} emptyText="No racks configured yet." />
      )}

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editTarget ? 'Edit Rack' : 'New Rack'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Name *</label>
              <input className="zip-input" required placeholder="Rack A"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Code *</label>
              <input className="zip-input uppercase font-mono" required placeholder="RACK-A" maxLength={20}
                value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                disabled={!!editTarget} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Type *</label>
              <select className="zip-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Company (optional)</label>
              <select className="zip-input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}>
                <option value="">Global (shared)</option>
                {clients.map((c) => <option key={c._id} value={c._id}>{c.name} ({c.code})</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-zip-muted">Floors *</label>
              <button type="button" onClick={addFloor} className="flex items-center gap-1 text-xs font-medium text-zip-red hover:text-[#C41424] transition-colors">
                <Plus size={12} /> Add Floor
              </button>
            </div>
            <div className="space-y-2">
              {form.floors.map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input className="zip-input flex-1" required placeholder="Floor label (e.g. F1)"
                    value={f.label} onChange={(e) => updateFloor(i, 'label', e.target.value)} />
                  <input type="number" min="0" className="zip-input w-28 font-mono" placeholder="Capacity"
                    value={f.capacity} onChange={(e) => updateFloor(i, 'capacity', e.target.value)} />
                  {form.floors.length > 1 && (
                    <button type="button" onClick={() => removeFloor(i)} className="text-zip-muted hover:text-zip-red transition-colors flex-shrink-0">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-zip-muted/70 mt-1">Capacity is optional.</p>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={submitting} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {submitting
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving…</>
                : editTarget ? 'Save Changes' : 'Create Rack'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default StorageSetup;
